import {useCurrentRepository} from '@github-ui/current-repository'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ReactionViewerLoading} from './components/ReactionViewerLoading'
import {VALUES} from './constants/values'
import {useAddReactionMutation} from './mutations/add-reaction-mutation'
import {useRemoveReactionMutation} from './mutations/remove-reaction-mutation'
import {reactionViewerQueryOptions} from './reaction-viewer-query'
import type {ReactionViewerGroupsFragment} from './reaction-viewer-types'
import {ReactionViewerBase} from './ReactionViewerBase'
import type {Reaction, ReactionContent, ReactionViewerGroup, Reactor} from './utils/ReactionGroups'

export type ReactionViewerProps = {
  reactionGroups: ReactionViewerGroupsFragment | null
  subjectId: string
  owner: string
  repo: string
  issueUpdatesChannel?: string | null
  liveUpdateTarget?: 'issue' | 'comment'
  canReact?: boolean
  showReactors?: boolean
}

type ReactionAliveNotification = {
  wait?: number
  issue_reaction_updated?: true
  reacted_comment_id?: string
}

const LIVE_UPDATE_THROTTLE_MS = 250

function getReactorTypeName(typeName: string): Reactor['typeName'] {
  switch (typeName) {
    case 'Bot':
    case 'Mannequin':
    case 'Organization':
    case 'User':
      return typeName
    default:
      return 'Other'
  }
}

export function ReactionViewer({
  reactionGroups: initialReactionGroups,
  subjectId,
  owner,
  repo,
  issueUpdatesChannel,
  liveUpdateTarget,
  canReact = true,
  showReactors = false,
}: ReactionViewerProps) {
  if (!owner || !repo) throw new Error('ReactionViewer requires a repository owner and name')

  const queryOptions = reactionViewerQueryOptions({
    owner,
    repo,
    subjectId,
    initialData: initialReactionGroups,
  })
  const reactionQuery = useQuery(queryOptions)
  const isReactionUpdated = useCallback(
    (data: ReactionAliveNotification) =>
      liveUpdateTarget === 'issue'
        ? data.issue_reaction_updated === true
        : liveUpdateTarget === 'comment' && data.reacted_comment_id === subjectId,
    [liveUpdateTarget, subjectId],
  )
  useInvalidateOnAliveUpdate<ReactionAliveNotification>(issueUpdatesChannel, queryOptions.queryKey, {
    filter: isReactionUpdated,
    throttleTimeout: LIVE_UPDATE_THROTTLE_MS,
  })
  const reactionGroupsData =
    useOptimisticData(reactionQuery.data, {
      targets: [{queryKey: queryOptions.queryKey, revalidate: false}],
    })?.node ?? initialReactionGroups
  const environment = useRelayEnvironment()
  const {mutate: addReaction} = useAddReactionMutation(environment)
  const {mutate: removeReaction} = useRemoveReactionMutation(environment)

  const onReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      if (!canReact) return
      const optimisticReactionGroups = reactionGroupsData?.reactionGroups?.map(reactionGroup => {
        if (!reactionGroup || reactionGroup.content !== reaction) return reactionGroup

        return {
          ...reactionGroup,
          viewerHasReacted: !viewerHasReacted,
          reactors: {
            ...reactionGroup.reactors,
            totalCount: Math.max(0, reactionGroup.reactors.totalCount + (viewerHasReacted ? -1 : 1)),
          },
        }
      })
      if (viewerHasReacted) {
        removeReaction({
          input: {subjectId, content: reaction, clientMutationId: null},
          optimisticFields: {reactionGroups: optimisticReactionGroups},
        })
      } else {
        addReaction({
          input: {subjectId, content: reaction, clientMutationId: null},
          optimisticFields: {reactionGroups: optimisticReactionGroups},
        })
      }
    },
    [addReaction, canReact, reactionGroupsData?.reactionGroups, removeReaction, subjectId],
  )

  if (reactionQuery.isPending) return <ReactionViewerLoading />
  if (reactionQuery.error) throw reactionQuery.error

  // filtering out null reaction groups which can be expected when there's an error on the server
  const reactionViewerGroups = (reactionGroupsData?.reactionGroups || [])
    .filter(reactionGroup => reactionGroup != null)
    .map((reactionGroup): ReactionViewerGroup => {
      const reaction: Reaction = {
        // Narrow away Relay's open-enum member; unknown reaction types are not renderable.
        content: reactionGroup.content as ReactionContent,
        viewerHasReacted: reactionGroup.viewerHasReacted,
      }

      const reactors: Reactor[] =
        reactionGroup.reactors?.nodes?.map(reactor => {
          const typeName = getReactorTypeName(reactor?.__typename || '')
          if (!reactor?.login || typeName === 'Other') {
            return {login: '', typeName: 'Other'}
          }
          let displayName = reactor.name || undefined
          let tooltipName = reactor.login
          if (reactor.__typename === 'Bot') {
            if (reactor.isCopilot) {
              displayName = VALUES.copilotDisplayName
              tooltipName = VALUES.copilotDisplayName
            } else if (reactor.isAgent && reactor.displayName) {
              displayName = reactor.displayName
              tooltipName = reactor.displayName
            }
          }
          return {
            login: reactor.login,
            avatarUrl: reactor.avatarUrl,
            displayName,
            tooltipName,
            typeName,
          }
        }) || []

      return {
        reaction,
        reactors,
        totalCount: reactionGroup.reactors?.totalCount || 0,
      }
    })

  return (
    <ReactionViewerBase
      reactionGroups={reactionViewerGroups}
      canReact={canReact}
      showReactors={showReactors}
      onReact={onReact}
    />
  )
}

export function ReactionViewerRelayQueryComponent({id, subjectLocked}: {id: string; subjectLocked?: boolean}) {
  const {ownerLogin, name} = useCurrentRepository()
  return (
    <ReactionViewer subjectId={id} owner={ownerLogin} repo={name} reactionGroups={null} canReact={!subjectLocked} />
  )
}

export default ReactionViewer
